import React, { useCallback, useContext, useEffect, useState } from "react";
import { Button, Col, Row, message } from "antd";
import { Editor } from "@tinymce/tinymce-react";
import {
  generateDischargeSummaryApi,
  getDischargeSummaryApi,
  submitDischargeSummaryApi,
} from "../../../../services/discharge.services";
import { getCookie } from "../../../../helpers/localStorage";
import { Role } from "../../../../constants/defaultKeys";
import useQuery from "../../../../hooks/useQuery";
import { UserContext } from "../../../../context/UserContext";
import NoDataPage from "../../../NoDataPages/NoDataPage";
import dischargeImage from "../../../../assets/images/nurseIcons/dischargeSummary.svg";
import ScaledHtmlPreview from "../../../ui/ScaledHtmlPreview";
import FormContentLoader from "../../FormsIp/FormContentLoader";
import ShareModal from "./ShareModal";
import FormHistoryCards from "../../FormsIp/CommonComponents/FormHistoryCards";
import { FORM_EDITOR_INIT } from "../../FormsIp/CommonComponents/tinymceConfig";
import {
  extractSummaryHistory,
  extractSummaryHtml,
} from "./dischargeSummaryData";
import "../../FormsIp/CommonComponents/FormDocument.css";
import "./DischargeSummaryToolbar.css";
import "./DischargeSummaryMobile.css";

export default function DischargeSummary({ patientData }) {
  const { userDetails } = useContext(UserContext);
  const params = useQuery();
  const role = getCookie(Role) || userDetails?.currRole;
  const [previewHtml, setPreviewHtml] = useState(null);
  const [history, setHistory] = useState([]);
  const [editorHtml, setEditorHtml] = useState("");
  const [value, setValue] = useState("");

  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [openingEditor, setOpeningEditor] = useState(false);
  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [openModal, setOpenModal] = useState(false);
  const [saveOnSend, setSaveOnSend] = useState(false);

  const fetchSummary = useCallback(() => {
    if (!params?.patient) return;
    setLoading(true);
    getDischargeSummaryApi(params.patient, `${role}&print=True`)
      .then((res) => {
        setPreviewHtml(extractSummaryHtml(res?.data));
        setHistory(extractSummaryHistory(res?.data));
      })
      .catch(() => {
        setPreviewHtml(null);
        setHistory([]);
      })
      .finally(() => setLoading(false));
  }, [params?.patient, role]);

  useEffect(() => {
    fetchSummary();
  }, [fetchSummary]);

  const generateSummary = async () => {
    try {
      setGenerating(true);
      await generateDischargeSummaryApi(params?.patient, role);
      fetchSummary();
    } catch (err) {
      console.error(err);
      message.error(
        err?.response?.data?.message ||
          "Unable to generate discharge summary. Please try again."
      );
    } finally {
      setGenerating(false);
    }
  };

  const startEditing = async () => {
    try {
      setOpeningEditor(true);
      const res = await getDischargeSummaryApi(params?.patient, role);
      const html = extractSummaryHtml(res?.data) || previewHtml || "";
      setEditorHtml(html);
      setValue(html);
      setEditing(true);
    } catch (err) {
      console.error(err);
      message.error(
        err?.response?.data?.message || "Unable to open the editor right now."
      );
    } finally {
      setOpeningEditor(false);
    }
  };

  const cancelEditing = () => {
    setValue(editorHtml);
    setEditing(false);
  };

  const saveSummary = async () => {
    try {
      setSaving(true);
      const response = await submitDischargeSummaryApi(params?.patient, role, {
        html: value,
      });
      message.success(response?.data?.message || "Discharge summary saved.");
      setEditing(false);
      fetchSummary();
      return true;
    } catch (error) {
      console.error("Error submitting content:", error);
      message.error(
        error?.response?.data?.message || "Failed to save. Please try again."
      );
      return false;
    } finally {
      setSaving(false);
    }
  };

  const saveAndShare = () => {
    setSaveOnSend(true);
    setOpenModal(true);
  };

  const closeShareModal = (open) => {
    setOpenModal(open);
    if (!open) setSaveOnSend(false);
  };

  return (
    <div className="discharge-summary-view">
      <FormContentLoader loading={loading} tip="Loading Discharge Summary...">
        {previewHtml ? (
          <>
            {!editing && (
              <div className="ds-history-row">
                <FormHistoryCards entries={history} />
                <div className="ds-history-row__actions">
                  <Button
                    className="ds-btn ds-btn--ghost"
                    onClick={startEditing}
                    loading={openingEditor}
                  >
                    Edit
                  </Button>
                  <Button
                    className="ds-btn ds-btn--primary"
                    onClick={() => {
                      setSaveOnSend(false);
                      setOpenModal(true);
                    }}
                  >
                    Share
                  </Button>
                </div>
              </div>
            )}

            {editing ? (
              <>
                <Editor
                  apiKey={process.env.REACT_APP_TINYMCE_API_KEY}
                  className="discharge-summary-editor"
                  init={FORM_EDITOR_INIT}
                  initialValue={editorHtml}
                  onEditorChange={(content) => setValue(content)}
                />
                <div className="ds-edit-footer">
                  <Button
                    className="ds-btn ds-btn--ghost"
                    onClick={cancelEditing}
                    disabled={saving}
                  >
                    Cancel
                  </Button>
                  <Button
                    className="ds-btn ds-btn--ghost"
                    onClick={saveSummary}
                    loading={saving}
                  >
                    Save
                  </Button>
                  <Button
                    className="ds-btn ds-btn--primary ds-btn--wide"
                    onClick={saveAndShare}
                    disabled={saving}
                  >
                    Save &amp; Share
                  </Button>
                </div>
              </>
            ) : (
              <ScaledHtmlPreview
                html={previewHtml}
                designWidth={800}
                className="ip-form-html-preview"
              />
            )}

            {openModal && (
              <ShareModal
                openModal={openModal}
                setOpenModal={closeShareModal}
                id={params?.patient}
                practitioner={role}
                patientData={patientData}
                onBeforeSend={saveOnSend ? saveSummary : undefined}
              />
            )}
          </>
        ) : (
          <Row>
            <Col span={24} style={{ textAlign: "center" }}>
              <NoDataPage
                title={"Discharge Summary Not generated"}
                subtitle={"Please click below to add a discharge summary"}
                image={dischargeImage}
              />
              <Button
                onClick={generateSummary}
                className="ds-btn ds-btn--primary"
                loading={generating}
              >
                Generate
              </Button>
            </Col>
          </Row>
        )}
      </FormContentLoader>
    </div>
  );
}
// plugins: [
//   "anchor",
//   "autolink",
//   "charmap",
//   "codesample",
//   "emoticons",
//   "image",
//   "link",
//   "lists",
//   "media",
//   "searchreplace",
//   "table",
//   "visualblocks",
//   "wordcount",
//   "checklist",
//   "mediaembed",
//   "casechange",
//   "export",
//   "formatpainter",
//   "pageembed",
//   "a11ychecker",
//   "tinymcespellchecker",
//   "permanentpen",
//   "powerpaste",
//   "advtable",
//   "advcode",
//   "editimage",
//   "advtemplate",
//   "mentions",
//   "tinycomments",
//   "tableofcontents",
//   "footnotes",
//   "mergetags",
//   "autocorrect",
//   "typography",
//   "inlinecss",
//   "markdown",
//   "importword",
//   "exportword",
//   "exportpdf",
// ],
